import { RefObject, useCallback, useEffect, useMemo, useState } from "react";
import { formatYmdLocal, isFiniteDate, toLocalMidnight } from "../config/utils";

export const useParsedSelectedDate = (selectedDate: Date | string): Date =>
  useMemo(() => {
    if (selectedDate instanceof Date) {
      return toLocalMidnight(selectedDate);
    }

    const [year, month, day] = selectedDate.split("-").map((part) => parseInt(part, 10));
    const parsedDate = new Date(year, month - 1, day);

    return isFiniteDate(parsedDate) ? toLocalMidnight(parsedDate) : toLocalMidnight(new Date());
  }, [selectedDate]);

export const useAvailableDatesSet = (availableDates: ReadonlyArray<Date | string>) =>
  useMemo(() => {
    const set = new Set<string>();

    availableDates.forEach((value) => {
      if (value instanceof Date) {
        set.add(formatYmdLocal(toLocalMidnight(value)));
        return;
      }

      set.add(value);
    });

    const hasDate = (date: Date) => set.has(formatYmdLocal(toLocalMidnight(date)));

    return { hasDate };
  }, [availableDates]);

interface UseCalendarToggleParams {
  containerRef: RefObject<HTMLElement>;
  disabled?: boolean;
}

export const useCalendarToggle = ({ containerRef, disabled = false }: UseCalendarToggleParams) => {
  const [calendarOpen, setCalendarOpen] = useState(false);

  const toggleCalendar = useCallback(() => {
    if (!disabled) {
      setCalendarOpen((prev) => !prev);
    }
  }, [disabled]);

  const closeCalendar = useCallback(() => setCalendarOpen(false), []);

  useEffect(() => {
    if (!calendarOpen) {
      return undefined;
    }

    const handlePointer = (event: Event) => {
      const target = event.target as Node | null;
      if (!target) return;

      if (!containerRef.current?.contains(target)) {
        closeCalendar();
      }
    };

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeCalendar();
      }
    };

    const handleScrollOrResize = () => {
      closeCalendar();
    };

    document.addEventListener("mousedown", handlePointer, true);
    document.addEventListener("touchstart", handlePointer, true);
    document.addEventListener("click", handlePointer, true);
    document.addEventListener("keydown", handleKey, true);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize, true);

    return () => {
      document.removeEventListener("mousedown", handlePointer, true);
      document.removeEventListener("touchstart", handlePointer, true);
      document.removeEventListener("click", handlePointer, true);
      document.removeEventListener("keydown", handleKey, true);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize, true);
    };
  }, [calendarOpen, closeCalendar, containerRef]);

  return { calendarOpen, toggleCalendar, closeCalendar };
};

